import { useEffect, useState, useContext, useRef } from 'react';
import classNames from 'classnames';
import * as FormActions from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import TextFieldHelperText from './TextFieldHelperText';
import type { RootState } from '../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FieldsetValidatorContext } from './Form';
import { validators } from '../../utils/validators';

interface CheckboxFieldProps {
  name: string;
  label: string;
  required: boolean;
  form: string;
  className?: string;
  hidden?: boolean;
}

const CheckboxField: React.FC<CheckboxFieldProps> = ({
  name,
  label,
  required,
  form,
  className,
  hidden,
}) => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(
    (state: RootState) => state.forms[form]?.[name]?.value
  );
  const fieldsetValidator = useContext(FieldsetValidatorContext);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const validate = () => {
    const errorMessages = errorMessage(
      { name, label, required },
      validators.required(value)
    );

    if (errorMessages) {
      setError(errorMessages);
      return false;
    }

    setError(null);
    return true;
  };

  useEffect(() => {
    const valid = validate();

    if (inputRef.current && value) {
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.blur();
      }, 500);
    }

    if (inputRef.current) {
      fieldsetValidator.register({
        name,
        required,
        valid,
        el: inputRef.current,
      });
    }
    return () => fieldsetValidator.unregister(name);
  }, [inputRef, value]);

  const handleChange = () => {
    dispatch(FormActions.updateField(form, name, !value));
  };

  const fieldClass = classNames({
    form__fieldholder: true,
    'form__fieldholder--error': error,
    'form__fieldholder--hasValue': value,
    'form__fieldholder--fullWidth': true,
    'form__fieldholder--hidden': hidden,
  });

  return (
    <div className={fieldClass}>
      <input
        type="checkbox"
        name={name}
        id={name}
        required={required}
        className={className}
        checked={!!value}
        onChange={handleChange}
        ref={inputRef}
      />
      <label htmlFor={name}>
        {required && label ? `${label} *` : label}
      </label>

      {error && (
        <>
          <TextFieldHelperText persistent validationMsg>
            {error}
          </TextFieldHelperText>
          <div className="validation-icon validation-wrong"></div>
        </>
      )}
      {value && <div className="validation-icon validation-ok"></div>}
    </div>
  );
};

export default CheckboxField;
